import { SchemaTypes } from '@allesauto/graphql';
import {
  SCREEN_BREAKPOINT,
  useDeviceTypeName,
  useScreenBreakpoint,
} from '@allesauto/utils/hooks';
import { ChevronLeft20, ChevronRight20 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useEffect, useRef, useState } from 'react';
import reactMultiCarousel, {
  ResponsiveType,
  StateCallBack,
} from 'react-multi-carousel';

import './Carousel.css';

import CarouselIndicator from './CarouselIndicator';
import CarouselThumbnails from './CarouselThumbnails';
import Button from '../button';
import { GalleryImage } from '../gallery';
import TextLink, { TextLinkProps } from '../textLink';
import { VehicleImageProps } from '../vehicleImage';

const Carousel: any =
  'default' in reactMultiCarousel
    ? (reactMultiCarousel as any).default // default export not recognized in next.js
    : reactMultiCarousel;

export interface CarouselProps {
  productCount?: number;
  itemsXLDesktop?: number;
  itemsToScrollXLDesktop?: number;
  itemsLDesktop?: number;
  itemsToScrollLDesktop?: number;
  itemsTable?: number;
  itemsToScrollTablet?: number;
  itemsMobile?: number;
  itemsToScrollMobile?: number;
  headlineTag?: 1 | 2 | 3 | 4 | 5;
  headline?: string;
  subHeadline?: string;
  textLink1Props?: TextLinkProps;
  textLink2Props?: TextLinkProps;
  isInfinite?: boolean;
  carouselItems?:
    | SchemaTypes.VehicleImage[]
    | SchemaTypes.ProductImage[]
    | GalleryImage[];
  selectedSlide?: number;
  classNames?: {
    dotListClass?: string;
    containerClass?: string;
    sectionClass?: string;
    headlineClass?: string;
    thumbnailClass?: string;
    itemClass?: string;
    buttonRightClass?: string;
    buttonLeftClass?: string;
    dotClass?: string;
  };
  hasThumbnailList?: boolean;
  hasTransition?: boolean;
  renderDisabledArrow?: boolean;
  imageAspectRatio?: VehicleImageProps['aspectRatio'];
  swipeable?: boolean;
  draggable?: boolean;
  transitionDuration?: string;
  handleAfterChange?: (value: boolean) => void;
  handleBeforeChange?: (value: boolean) => void;
  handleSlideChange?: (value: number) => void;
  typeTestId?: string;
  deviceType?: 'mobile' | 'desktop';
  darkBackground?: boolean;
  partialVisible?: boolean;
  partialVisibilityGutter?: number;
  removeArrowOnDeviceType?: string[];
  responsive?: ResponsiveType;
  showDots?: boolean;
  arrows?: boolean;
  afterChange?: (previousSlide: number, state: StateCallBack) => void;
}

interface CustomArrowProps {
  onClick?: () => void;
  isLeft?: boolean;
  disabled?: boolean;
}

const CarouselComponent: FC<React.PropsWithChildren<CarouselProps>> = ({
  children,
  itemsXLDesktop = 4,
  itemsToScrollXLDesktop = 4,
  itemsLDesktop = 4,
  itemsToScrollLDesktop = 4,
  itemsTable = 2,
  itemsToScrollTablet = 2,
  itemsMobile = 1,
  itemsToScrollMobile = 1,
  headlineTag = 1,
  headline,
  subHeadline,
  textLink1Props,
  textLink2Props,
  isInfinite = true,
  carouselItems,
  selectedSlide = 0,
  classNames,
  hasThumbnailList = false,
  imageAspectRatio,
  swipeable = true,
  draggable = true,
  handleAfterChange,
  handleBeforeChange,
  handleSlideChange,
  hasTransition = true,
  transitionDuration = 'lg:!duration-1000',
  renderDisabledArrow = true,
  typeTestId,
  removeArrowOnDeviceType = ['mobile', 'tablet'],
  partialVisible = false,
  partialVisibilityGutter = 0,
  darkBackground = false,
  responsive = {},
  productCount,
  ...props
}) => {
  if (productCount != undefined) {
    if (productCount < 4) {
      itemsLDesktop = productCount;
      itemsToScrollLDesktop = productCount;
      itemsXLDesktop = productCount;
      itemsToScrollXLDesktop = productCount;
    }
  }
  children = React.Children.toArray(children).filter(
    (child) => child !== undefined
  );
  const responsiveBreakpoints = {
    desktopLarge: {
      breakpoint: { max: 9999, min: SCREEN_BREAKPOINT.XL },
      items: itemsXLDesktop,
      slidesToSlide: itemsToScrollXLDesktop,
      partialVisibilityGutter: 0,
    },
    desktop: {
      breakpoint: { max: SCREEN_BREAKPOINT.XL - 1, min: SCREEN_BREAKPOINT.LG },
      items: itemsLDesktop,
      slidesToSlide: itemsToScrollLDesktop,
      partialVisibilityGutter: 0,
    },
    tablet: {
      breakpoint: { max: SCREEN_BREAKPOINT.LG - 1, min: SCREEN_BREAKPOINT.MD },
      items: itemsTable,
      slidesToSlide: itemsToScrollTablet,
      partialVisibilityGutter: partialVisibilityGutter,
    },
    mobile: {
      breakpoint: { max: SCREEN_BREAKPOINT.MD - 1, min: SCREEN_BREAKPOINT.SM },
      items: itemsMobile,
      slideToSlide: itemsToScrollMobile,
      partialVisibilityGutter: partialVisibilityGutter,
    },
    ...responsive,
  };

  const carouselRef = useRef<typeof Carousel>(null);
  const breakpoint = useScreenBreakpoint();
  const carouselDeviceType = useDeviceTypeName();

  useEffect(() => {
    if (carouselRef && carouselRef.current) {
      carouselRef.current.goToSlide(selectedSlide);
    }
  }, [selectedSlide, carouselRef]);

  const [verifyNumberitems, setVerifyNumbers] = useState<boolean>(false);
  const numberOfChildren = React.Children.count(children);
  const hasTextLink1 = textLink1Props?.text && textLink1Props?.onClick;
  const hasTextLink2 = textLink2Props?.text && textLink2Props?.onClick;
  const hasAtLeastOneTextLink = hasTextLink1 || hasTextLink2;

  useEffect(() => {
    if (breakpoint >= SCREEN_BREAKPOINT.XL) {
      setVerifyNumbers(numberOfChildren === itemsXLDesktop);
    } else if (
      breakpoint <= SCREEN_BREAKPOINT.LG &&
      breakpoint > SCREEN_BREAKPOINT.MD
    ) {
      setVerifyNumbers(numberOfChildren === itemsLDesktop);
    } else if (
      breakpoint <= SCREEN_BREAKPOINT.MD &&
      breakpoint > SCREEN_BREAKPOINT.SM
    ) {
      setVerifyNumbers(numberOfChildren === itemsTable);
    } else if (breakpoint <= SCREEN_BREAKPOINT.SM) {
      setVerifyNumbers(numberOfChildren === itemsMobile);
    }
  }, [
    breakpoint,
    itemsLDesktop,
    itemsMobile,
    itemsTable,
    itemsXLDesktop,
    numberOfChildren,
  ]);

  const hasOneElementToShow =
    itemsXLDesktop === 1 &&
    itemsLDesktop === 1 &&
    itemsTable === 1 &&
    itemsMobile === 1;

  const className = {
    sectionClass: clsx(
      'bg-white grid grid-cols-24 mb-0 relative lg:pb-100px',
      !headline && !hasAtLeastOneTextLink && 'pb-80px',
      !hasAtLeastOneTextLink && 'sm:pb-60px md:pb-80px',
      classNames?.sectionClass,
      carouselItems && carouselItems.length <= 1 && '!pb-0'
    ),
    itemClass: clsx(
      'flex',
      hasOneElementToShow ? 'md:px-0 lg:px-0 w-1/2' : 'md:px-20px lg:px-30px',
      classNames?.itemClass
    ),
    containerClass: clsx(
      'col-end-24 col-start-2 pb-40px lg:!static lg:col-end-23 lg:col-start-3',
      !headline && !hasAtLeastOneTextLink && 'mt-60px lg:mt-80px',
      numberOfChildren < (itemsXLDesktop || itemsLDesktop)
        ? 'md:pb-0'
        : 'md:pb-60px lg:pb-80px',
      classNames?.containerClass
    ),
    dotListClass: clsx(
      ' col-end-24 col-start-2 w-full md:col-end-18 md:col-start-8 md:px-20px lg:pb-100px lg:px-0',
      classNames?.dotListClass
    ),
    sliderClass: clsx(hasTransition ? transitionDuration : '!transition-none'),
    headlineClass: clsx(
      'col-end-23 col-start-2 lg:col-end-14 lg:col-start-2',
      headlineTag === 1
        ? 'my-60px md:my-80px lg:my-100px'
        : headlineTag === 4
        ? 'my-40px'
        : 'mb-40px mt-60px md:mb-60px md:mt-80px lg:mb-80px lg:mt-100px',
      classNames?.headlineClass
    ),
    headlineTextClass: clsx(
      'break-words font-headline font-weight-bold line-clamp overflow-hidden text-ellipsis',
      headlineTag === 1
        ? 'text-title md:text-headline1 text-purple80 monochrome:text-grey90'
        : headlineTag === 2
        ? 'text-headline2 text-purple80 monochrome:text-grey90'
        : headlineTag === 3
        ? 'text-headline4 text-black'
        : 'text-headline4 text-black'
    ),
    subHeadlineClass:
      'text-p1 mt-10px text-grey80 font-regular font-weight-normal',
    textLinkContainerClass: clsx(
      'col-end-21 col-start-2 flex flex-wrap items-center justify-start order-last text-right md:ml-20px lg:col-end-24 lg:col-start-16 lg:justify-end lg:ml-0 lg:order-none',
      headlineTag === 1
        ? 'my-60px md:my-80px lg:my-100px'
        : headlineTag === 4
        ? 'my-40px'
        : 'mb-40px mt-60px md:mb-60px md:mt-80px lg:mb-80px lg:mt-100px'
    ),
    textLink1Class: clsx(hasTextLink2 && 'py-10px'),
    textLink2Class: clsx(hasTextLink1 && 'ml-25px md:ml-30px py-10px'),
    buttonLeftClass: clsx(
      'absolute left-0 md:col-end-3 md:col-start-2',
      classNames?.buttonLeftClass
    ),
    buttonRightClass: clsx(
      'absolute right-0 md:col-end-24 md:col-start-23',
      classNames?.buttonRightClass
    ),
  };

  const CustomArrow = ({ onClick, isLeft, disabled }: CustomArrowProps) => {
    // The props get populated by a callback from the Carousel (react-multi-carousel) except for isLeft

    // Work-around for aurego Todo: Fix when we have an optimized solution
    const auregoButtonLeftClass = clsx(
      'absolute aurego-color1 left-0 md:col-end-3 md:col-start-2',
      classNames?.buttonLeftClass
    );
    const auregoButtonRightClass = clsx(
      'absolute aurego-color1 right-0 md:col-end-24 md:col-start-23',
      classNames?.buttonRightClass
    );

    return (
      <Button
        customClassName={
          isLeft
            ? disabled
              ? className.buttonLeftClass
              : auregoButtonLeftClass
            : `${
                disabled ? className.buttonRightClass : auregoButtonRightClass
              } `
        }
        icon={isLeft ? <ChevronLeft20 /> : <ChevronRight20 />}
        whitelabel={true}
        onClick={onClick}
        disabled={disabled}
      />
    );
  };

  const HeadlineElement = `h${headlineTag}` as keyof JSX.IntrinsicElements;
  return (
    <div className={className.sectionClass}>
      {headline && (
        <div className={className.headlineClass}>
          <HeadlineElement
            className={className.headlineTextClass}
            id={`label--${typeTestId}`}
          >
            {headline}
          </HeadlineElement>
          {subHeadline && (
            <p className={className.subHeadlineClass}>{subHeadline}</p>
          )}
        </div>
      )}

      {hasAtLeastOneTextLink ? (
        <div className={className.textLinkContainerClass}>
          {hasTextLink1 && (
            <div className={className.textLink1Class}>
              <TextLink {...textLink1Props} />
            </div>
          )}
          {hasTextLink2 && (
            <div className={className.textLink2Class}>
              <TextLink {...textLink2Props} />
            </div>
          )}
        </div>
      ) : null}

      <Carousel
        ref={carouselRef}
        partialVisible={partialVisible}
        removeArrowOnDeviceType={removeArrowOnDeviceType}
        responsive={responsiveBreakpoints}
        infinite={isInfinite}
        draggable={draggable}
        swipeable={swipeable}
        deviceType={carouselDeviceType}
        autoPlay={carouselDeviceType === 'mobile'}
        autoPlaySpeed={5000}
        ssr
        customButtonGroup={
          hasThumbnailList ? (
            <CarouselThumbnails
              carouselItems={carouselItems}
              imageAspectRatio={imageAspectRatio}
              classNames={classNames?.thumbnailClass}
              handleSlideChange={handleSlideChange}
            />
          ) : null
        }
        renderButtonGroupOutside={true}
        customDot={
          <CarouselIndicator
            customClassName={classNames?.dotClass}
            darkBackground={darkBackground}
          />
        }
        dotListClass={className.dotListClass}
        containerClass={className.containerClass}
        itemClass={className.itemClass}
        arrows={!verifyNumberitems}
        sliderClass={className.sliderClass}
        renderArrowsWhenDisabled={renderDisabledArrow}
        customRightArrow={<CustomArrow />}
        customLeftArrow={<CustomArrow isLeft />}
        showDots={hasThumbnailList ? false : !verifyNumberitems}
        beforeChange={() => handleBeforeChange && handleBeforeChange(true)}
        afterChange={() => handleAfterChange && handleAfterChange(false)}
        {...props}
      >
        {children}
      </Carousel>
    </div>
  );
};

export default CarouselComponent;
